import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import SubscriptionService from '../services/SubscriptionService';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { isIOS, isAndroid } from '../utils/platformUtils';
import { validateEmail, validatePassword, validatePasswordConfirmation, validateName, validateOrganizationCode, sanitizeInput } from '../utils/validationUtils';
import { useError } from '../contexts/ErrorContext';
import PlatformLayout from './PlatformLayout';
import PlatformInput from './PlatformInput';
import PlatformButton from './PlatformButton';
import { useTranslation } from '../hooks/useTranslation';
import styles from './RegisterForm.module.css';
import LanguageSwitcher from './LanguageSwitcher';


function RegisterForm() {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const isNativeIOS = isNative && isIOS();
  const { t: tAuth } = useTranslation('auth');
  const { t: tForms } = useTranslation('forms');
  const { showErrorToast } = useError();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'Salesperson',
    organizationCode: '',
    termsAccepted: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Convert organization code to uppercase
    const finalValue = name === 'organizationCode' ? value.toUpperCase() : value;
    setFormData({...formData, [name]: finalValue});
    
    // Clear error when user starts typing
    setError('');
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    
    // Real-time validation for email
    if (name === 'email' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, email: emailValidation.error }));
      } else {
        setFieldErrors(prev => ({ ...prev, email: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setError('');
    setFieldErrors({});
    
    // Comprehensive validation
    const emailValidation = validateEmail(formData.email);
    const passwordValidation = validatePassword(formData.password);
    const passwordConfirmationValidation = validatePasswordConfirmation(formData.password, formData.confirmPassword);
    const nameValidation = validateName(formData.fullName, 'Full Name', {
      allowAlphanumeric: false // Standard name validation for full names
    });
    const orgCodeValidation = validateOrganizationCode(formData.organizationCode);
    
    // Collect all validation errors
    const newFieldErrors = {};
    let hasErrors = false;
    
    if (!emailValidation.isValid) {
      newFieldErrors.email = emailValidation.error;
      hasErrors = true;
    }
    
    if (!passwordValidation.isValid) {
      newFieldErrors.password = passwordValidation.error;
      hasErrors = true;
    }
    
    if (!passwordConfirmationValidation.isValid) {
      newFieldErrors.confirmPassword = passwordConfirmationValidation.error;
      hasErrors = true;
    }
    
    if (!nameValidation.isValid) {
      newFieldErrors.fullName = nameValidation.error;
      hasErrors = true;
    }
    
    if (!orgCodeValidation.isValid) {
      newFieldErrors.organizationCode = orgCodeValidation.error;
      hasErrors = true;
    }
    
    // Validate Terms acceptance
    if (!formData.termsAccepted) {
      newFieldErrors.termsAccepted = tAuth('legal.acceptTermsRequired');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setError(tAuth('register.error.fixErrors'));
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      // Sanitize inputs before processing
      const sanitizedData = {
        email: sanitizeInput(formData.email).toLowerCase(),
        fullName: sanitizeInput(formData.fullName),
        organizationCode: sanitizeInput(formData.organizationCode).toUpperCase(),
        password: formData.password, // Don't sanitize password
        role: formData.role
      };
      
      // First check if the organization code exists
      const orgCodeRef = doc(db, 'organizationCodes', sanitizedData.organizationCode);
      const orgCodeDoc = await getDoc(orgCodeRef);
      
      if (!orgCodeDoc.exists()) {
        setFieldErrors(prev => ({ ...prev, organizationCode: tAuth('register.error.invalidOrgCode') }));
        setError(tAuth('register.error.invalidOrgCode'));
        setIsLoading(false);
        return;
      }

      const organizationId = orgCodeDoc.data().organizationId;
      
      // Check user limit after getting organizationId
      const userLimit = await SubscriptionService.checkUserLimit(organizationId);
      if (!userLimit.allowed) {
        showErrorToast(tAuth('register.error.userLimitReached', { current: userLimit.current, limit: userLimit.limit }), {
          variant: 'warning',
          autoHide: true,
          duration: 6000
        });
        setIsLoading(false);
        return;
      }

      // Check if organization exists
      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        setError(tAuth('register.error.organizationNotFound'));
        setIsLoading(false);
        return;
      }

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, sanitizedData.email, sanitizedData.password);
      const user = userCredential.user;

      // Update user profile with name
      await updateProfile(user, { displayName: sanitizedData.fullName });
      
      // Create user document in /users collection
      const acceptedAt = new Date().toISOString();
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        email: sanitizedData.email,
        fullName: sanitizedData.fullName,
        organizationId: organizationId,
        createdAt: acceptedAt,
        termsAcceptedAt: acceptedAt,
        privacyAcceptedAt: acceptedAt
      });
      
      // Verify user document was created
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        showErrorToast(tAuth('register.error.failedToCreateUserProfile'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // Create user document in organization's users collection
      const orgUserRef = doc(db, `organizations/${organizationId}/users/${user.uid}`);
      await setDoc(orgUserRef, {
        email: sanitizedData.email,
        fullName: sanitizedData.fullName,
        role: sanitizedData.role,
        approved: false, // Users need approval from business owner
        createdAt: acceptedAt,
        termsAcceptedAt: acceptedAt,
        privacyAcceptedAt: acceptedAt
      });
      
      // Verify organization user document was created
      const orgUserDoc = await getDoc(orgUserRef);
      if (!orgUserDoc.exists()) {
        showErrorToast(tAuth('register.error.failedToCreateOrgUserProfile'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      // Update totalUsers count in organization
      try {
        const { updateOrganizationUserCount } = await import('../services/userCountService');
        await updateOrganizationUserCount(organizationId);
      } catch (err) {
        // Failed to update organization user count - handled silently
      }

      // Navigate to pending approval page instead of signing out
      navigate('/pending-approval', { 
        state: { 
          message: tAuth('register.success.pendingApproval') 
        } 
      });
    } catch (error) {
      // Handle specific Firebase errors
      if (error.code === 'auth/invalid-email') {
        setFieldErrors(prev => ({ ...prev, email: tAuth('register.error.invalidEmail') }));
        setError(tAuth('register.error.invalidEmail'));
      } else if (error.code === 'auth/email-already-in-use') {
        setFieldErrors(prev => ({ ...prev, email: tAuth('register.error.emailAlreadyInUse') }));
        setError(tAuth('register.error.emailAlreadyInUse'));
      } else if (error.code === 'auth/weak-password') {
        setFieldErrors(prev => ({ ...prev, password: tAuth('register.error.weakPassword') }));
        setError(tAuth('register.error.weakPassword'));
      } else {
        setError(error.message);
      }
      // Try to sign out on error
      try {
        await auth.signOut();
      } catch (e) {
        // Error signing out after registration error - handled silently
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render different layouts based on platform
  if (isNative) {
    return (
      <PlatformLayout>
        <div className={styles.formContainer}>
          <div className={`${styles.formCard} fade-in`}>
            {/* Language Switcher - Subtle placement */}
            <LanguageSwitcher 
              variant="toggle" 
              size="small" 
              position="top-right"
            />
            
            <div style={{ marginBottom: '32px' }}>
              <h1 className={styles.formTitle}>{tAuth('register.title')}</h1>
              <p className={styles.formSubtitle}>{tAuth('register.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} style={{ width: '100%' }} className={`${styles.formWrapper} form-simple`}>
              {error && (
                <div style={{
                  backgroundColor: isIOS() ? '#FFE5E5' : isAndroid() ? '#FFEBEE' : '#FFEBEE',
                  color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                  padding: '12px 16px',
                  borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  border: `1px solid ${isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F'}`
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="text"
                  name="organizationCode"
                  placeholder={tAuth('register.organizationCode')}
                  value={formData.organizationCode}
                  onChange={handleChange}
                  required
                  maxLength="6"
                  error={fieldErrors.organizationCode}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="text"
                  name="fullName"
                  placeholder={tAuth('register.fullName')}
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  error={fieldErrors.fullName}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="email"
                  name="email"
                  placeholder={tAuth('register.email')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={fieldErrors.email}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="password"
                  name="password"
                  placeholder={tAuth('register.password')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  error={fieldErrors.password}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <PlatformInput
                  type="password"
                  name="confirmPassword"
                  placeholder={tAuth('register.confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  error={fieldErrors.confirmPassword}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: isIOS() ? '10px' : isAndroid() ? '4px' : '8px',
                    border: `1px solid ${isIOS() ? '#C7C7CC' : isAndroid() ? '#BDBDBD' : '#E0E0E0'}`,
                    fontSize: '16px',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <option value="Salesperson">{tAuth('register.roles.salesperson')}</option>
                  <option value="Accountant">{tAuth('register.roles.accountant')}</option>
                  <option value="InventoryManager">{tAuth('register.roles.inventoryManager')}</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: isIOS() ? '#000000' : isAndroid() ? '#212121' : '#333333',
                  lineHeight: '1.5'
                }}>
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={(e) => {
                      setFormData({...formData, termsAccepted: e.target.checked});
                      setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                    }}
                    style={{
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    {tAuth('legal.acceptTerms')}{' '}
                    <Link
                      to="/terms-of-service"
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tAuth('legal.termsLink')}
                    </Link>
                    {' '}{tAuth('legal.acceptTermsAnd')}{' '}
                    <Link
                      to="/privacy-policy"
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tAuth('legal.privacyLink')}
                    </Link>
                  </span>
                </label>
                {fieldErrors.termsAccepted && (
                  <div style={{
                    color: isIOS() ? '#FF3B30' : isAndroid() ? '#B00020' : '#D32F2F',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '30px'
                  }}>
                    {fieldErrors.termsAccepted}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '2px' }}>
                <PlatformButton
                  type="submit"
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  {isLoading ? tAuth('register.creatingAccount') : tAuth('register.createAccount')}
                </PlatformButton>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '4px', width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ color: isIOS() ? '#8E8E93' : isAndroid() ? '#757575' : '#666666' }}>
                    {tAuth('register.alreadyHaveAccount')}{' '}
                  </span>
                  <Link
                    to="/login"
                    style={{
                      color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    {tAuth('register.signIn')}
                  </Link>
                </div>
                {!isNativeIOS && (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ color: isIOS() ? '#8E8E93' : isAndroid() ? '#757575' : '#666666' }}>
                      {tAuth('register.needToCreateOrg')}{' '}
                    </span>
                    <Link
                      to="/register-organization"
                      style={{
                        color: isIOS() ? '#007AFF' : isAndroid() ? '#007AFF' : '#1976D2',
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}
                    >
                      {tAuth('register.registerBusiness')}
                    </Link>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  // Web version (existing code)
  return (
    <div className="container">
      <div className={!isNative ? "auth-container" : ""}>
        {/* Language Switcher - Subtle placement for web */}
        <LanguageSwitcher 
          variant="toggle" 
          size="small" 
          position="top-right"
        />
        
        <div className="auth-header">
          <h1>{tAuth('register.title')}</h1>
          <p className="auth-subtitle">{tAuth('register.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className={`${styles.formWrapper} auth-form form-simple`}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="organizationCode">{tForms('labels.organizationCode')}</label>
            <input
              type="text"
              id="organizationCode"
              name="organizationCode"
              value={formData.organizationCode}
              onChange={handleChange}
              placeholder={tForms('placeholders.organizationCode')}
              required
              disabled={isLoading}
              maxLength="6"
              pattern="[A-Z0-9]{6}"
              title="6-character organization code (letters and numbers only)"
              error={fieldErrors.organizationCode}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="fullName">{tForms('labels.fullName')}</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={tForms('placeholders.fullName')}
              required
              disabled={isLoading}
              error={fieldErrors.fullName}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="email">{tForms('labels.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={tForms('placeholders.email')}
              required
              disabled={isLoading}
              error={fieldErrors.email}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password">{tForms('labels.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={tForms('placeholders.password')}
              required
              disabled={isLoading}
              minLength="6"
              error={fieldErrors.password}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="confirmPassword">{tForms('labels.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={tForms('placeholders.confirmPassword')}
              required
              disabled={isLoading}
              minLength="6"
              error={fieldErrors.confirmPassword}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="role">{tForms('labels.role')}</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={isLoading}
            >
              <option value="Salesperson">{tForms('roles.salesperson')}</option>
              <option value="Accountant">{tForms('roles.accountant')}</option>
              <option value="InventoryManager">{tForms('roles.inventoryManager')}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              lineHeight: '1.5',
              marginBottom: fieldErrors.termsAccepted ? '0.25rem' : '0'
            }}>
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={(e) => {
                  setFormData({...formData, termsAccepted: e.target.checked});
                  setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                }}
                style={{
                  marginTop: '2px',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: 'var(--primary-color)',
                  flexShrink: 0
                }}
              />
              <span style={{ flex: 1 }}>
                {tAuth('legal.acceptTerms')}{' '}
                <Link
                  to="/terms-of-service"
                  className="auth-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {tAuth('legal.termsLink')}
                </Link>
                {' '}{tAuth('legal.acceptTermsAnd')}{' '}
                <Link
                  to="/privacy-policy"
                  className="auth-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {tAuth('legal.privacyLink')}
                </Link>
              </span>
            </label>
            {fieldErrors.termsAccepted && (
              <div className="field-error" style={{ marginLeft: '1.75rem' }}>
                {fieldErrors.termsAccepted}
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className={styles.submit}
            disabled={isLoading}
          >
            {isLoading ? tForms('messages.saving') : tForms('buttons.create')}
          </button>

          <div className="auth-footer">
            <p>
              {tAuth('register.alreadyHaveAccount')}{' '}
              <Link to="/login" className="auth-link">
                {tAuth('register.signInHere')}
              </Link>
            </p>
            {!isNativeIOS && (
              <p>
                {tAuth('register.needOrganization')}{' '}
                <Link to="/register-organization" className="auth-link">
                  {tAuth('register.registerBusiness')}
                </Link>
              </p>
            )}
            <div className="legal-links-footer">
              <Link to="/privacy-policy" className="legal-link">
                {tAuth('legal.privacyPolicy')}
              </Link>
              <span className="legal-separator">•</span>
              <Link to="/terms-of-service" className="legal-link">
                {tAuth('legal.termsOfService')}
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterForm;
